import { useQuery } from "react-query";
import {
  Box,
  Heading,
  SimpleGrid,
  Text,
  VStack,
  Spinner,
  useColorModeValue,
  Select,
  Stack,
  Button,
  Flex,
  Divider,
} from "@chakra-ui/react";
import { fetchStudentQuestionPapers } from "API/questionpaperAPI";
import { getSubjects } from "API/subjectApi";
import { getStudentById } from "API/studentApi";
import { useState } from "react";
import { useHistory } from "react-router-dom";
import { useAppSelector } from "app/hook"; 

interface QuestionPaper {
  _id: string;
  title: string;
  subject: string;
  duration: number;
  totalMarks: number;
  programName?: string;
  programId: string;
  sections: Array<{
    name: string;
    questions: Array<{
      marks: number;
    }>;
  }>;
}

interface Student {
  _id: string;
  name: string;
  gender: string;
  interestedProgram: {
    _id: string;
    name: string;
  };
  interestedPrograms: any[];
  activePrograms?: Array<{
    program: {
      _id: string;
      name: string;
    };
  }>;
}

interface QuestionPapersResponse {
  papers: QuestionPaper[];
}

const StudentQuestionPapers = () => {
  const [selectedSubject, setSelectedSubject] = useState("");
  const history = useHistory();
  const bgCard = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  const { uid } = useAppSelector((state) => state.auth);
  const { user } = useAppSelector((state) => state.auth);

  const { data: subjects = [] } = useQuery("subjects", getSubjects);

  // Get student info for program data
  const { 
    data: student, 
    isLoading: studentLoading 
  } = useQuery<Student>(["student", uid], () => getStudentById(uid!), {
    enabled: !!uid,
  });

  // Get question papers
  const {
    data: questionPapersData,
    isLoading: papersLoading,
    error,
  } = useQuery<QuestionPapersResponse, Error>(
    ["studentPapers", uid],
    () => fetchStudentQuestionPapers(uid!),
    {
      enabled: !!uid,
    }
  );

  const questionPapers = questionPapersData?.papers || [];
  const isLoading = studentLoading || papersLoading;

  // Extract program info - try multiple sources
  const userProgramId = student?.interestedProgram?._id || 
                       student?.activePrograms?.[0]?.program?._id || 
                       user?.activeProgramId;
  const userProgramName = student?.interestedProgram?.name || 
                         student?.activePrograms?.[0]?.program?.name;

  // Debug logging
  console.log("=== DEBUG INFO ===");
  console.log("questionPapersData:", questionPapersData);
  console.log("questionPapers:", questionPapers);
  console.log("student:", student);
  console.log("user:", user);
  console.log("userProgramId:", userProgramId);
  console.log("userProgramName:", userProgramName);
  console.log("questionPapers length:", questionPapers.length);
  console.log("student?.interestedProgram:", student?.interestedProgram);
  console.log("student?.activePrograms:", student?.activePrograms);
  console.log("user?.activeProgramId:", user?.activeProgramId);

  // Filter papers by user's program
  const programFilteredPapers = userProgramId 
    ? questionPapers.filter((paper: QuestionPaper) => {
        console.log(`Paper ${paper.title} programId: ${paper.programId}, userProgramId: ${userProgramId}, match: ${paper.programId === userProgramId}`);
        return paper.programId === userProgramId;
      })
    : questionPapers; // Show all papers if no program ID found

  console.log("programFilteredPapers:", programFilteredPapers);
  console.log("programFilteredPapers length:", programFilteredPapers.length);

  // TEMPORARY: If no papers after filtering, show all papers for debugging
  const finalPapers = programFilteredPapers.length === 0 && questionPapers.length > 0 
    ? questionPapers 
    : programFilteredPapers;
  
  console.log("finalPapers:", finalPapers);
  console.log("finalPapers length:", finalPapers.length);

  // Filter by subject
  const filteredPapers = selectedSubject
    ? finalPapers.filter((paper: QuestionPaper) => paper.subject === selectedSubject)
    : finalPapers;

  return (
    <Box p={4}>
      <Heading size="lg" mb={6}>
        Available Question Papers
      </Heading>

      <Stack direction={["column", "row"]} mb={6} spacing={4}>
        <Select
          placeholder="Filter by Subject"
          value={selectedSubject}
          onChange={(e) => setSelectedSubject(e.target.value)}
          w={["100%", "300px"]}
        >
          {subjects.map((sub: any) => (
            <option key={sub._id} value={sub.name}>
              {sub.name.toUpperCase()}
            </option>
          ))}
        </Select>
        
        {selectedSubject && (
          <Button size="sm" variant="outline" onClick={() => setSelectedSubject("")}>
            Clear Filter
          </Button>
        )}
      </Stack>

      {isLoading ? (
        <Flex justify="center" align="center" mt={10}>
          <Spinner size="lg" />
        </Flex>
      ) : error ? (
        <Text color="red.500">Failed to load papers: {error.message}</Text>
      ) : !userProgramId ? (
        <Box bg="orange.100" p={4} borderRadius="md">
          <Text color="orange.800" fontWeight="bold">
            ⚠️ No program assigned
          </Text>
          <Text color="orange.700" fontSize="sm" mt={1}>
            Please contact support to assign a program to your account.
          </Text>
        </Box>
      ) : filteredPapers.length === 0 ? (
        <Box bg="blue.50" p={4} borderRadius="md">
          <Text color="blue.800" fontWeight="bold">
            📚 No question papers available
          </Text>
          <Text color="blue.700" fontSize="sm" mt={1}>
            {selectedSubject 
              ? `No papers found for ${userProgramName} program in ${selectedSubject} subject.`
              : `No papers found for ${userProgramName} program.`
            }
          </Text>
        </Box>
      ) : (
        <>

          <SimpleGrid columns={[1, 2, 3]} spacing={6}>
            {filteredPapers.map((paper: QuestionPaper) => {
              const programName = paper.programName || userProgramName || "Unknown Program";

              const sectionSummaries = (paper.sections || []).map((section) => {
                const sectionMarks = (section.questions || []).reduce(
                  (acc: number, q) => acc + Number(q.marks || 0),
                  0
                );
                return {
                  name: section.name,
                  count: (section.questions || []).length,
                  marks: sectionMarks,
                };
              });

              return (
                <Box
                  key={paper._id}
                  p={6}
                  bg={bgCard}
                  borderWidth="1px"
                  borderColor={borderColor}
                  borderRadius="lg"
                  shadow="md"
                  transition="all 0.2s"
                  _hover={{ shadow: "lg", transform: "translateY(-2px)" }}
                >
                  <VStack align="start" spacing={4}>
                    <Heading size="md" textAlign="center" w="100%" noOfLines={2}>
                      {(paper.title || "Untitled Paper").toUpperCase()}
                    </Heading>

                    <Divider />

                    <Flex justify="space-between" w="100%">
                      <Text fontSize="sm" fontWeight="semibold">
                        SUBJECT: {(paper.subject || "Unknown").toUpperCase()}
                      </Text>
                    </Flex>

                    <Flex justify="space-between" w="100%">
                      <Text fontSize="sm">
                        <strong>DURATION:</strong> {paper.duration} mins
                      </Text>
                      <Text fontSize="sm">
                        <strong>TOTAL MARKS:</strong> {paper.totalMarks}
                      </Text>
                    </Flex>

                    {sectionSummaries.length > 0 && (
                      <Box w="100%">
                        <Text fontSize="sm" fontWeight="bold" mb={2}>
                          SECTIONS:
                        </Text>
                        <VStack align="start" spacing={1}>
                          {sectionSummaries.map((s, idx) => (
                            <Flex key={idx} justify="space-between" w="100%" fontSize="xs">
                              <Text>{s.name}</Text>
                              <Text>
                                {s.count} Questions | {s.marks} Marks
                              </Text>
                            </Flex>
                          ))}
                        </VStack>
                      </Box>
                    )}

                    <Divider />

                    <Button
                      size="md"
                      colorScheme="teal"
                      w="100%"
                      onClick={() =>
                        history.push(`/student/dashboard/attempt/${paper._id}`)
                      }
                    >
                      START EXAM
                    </Button>
                  </VStack>
                </Box>
              );
            })}
          </SimpleGrid>
        </>
      )}
    </Box>
  );
};

export default StudentQuestionPapers;
